import { useEffect, useRef, useState } from 'react';
import { READINESS_MAX_ACCURACY_M } from '../shared/constants';
import { useGame } from './state/SocketProvider';
import { useGeoWatch, setSimPosition } from './net/geo';
import { shouldAlert, fireAlert } from './net/alert';
import { subscribePush } from './net/push';
import { socket } from './net/socket';
import type { PublicState } from '../shared/protocol';
import { useSim } from './state/sim';
import { Landing } from './screens/Landing';
import { Lobby } from './screens/Lobby';
import { FirstHider } from './screens/FirstHider';
import { Round } from './screens/Round';
import { Score } from './screens/Score';
import { HowToPlay } from './screens/HowToPlay';
import { CardStudio } from './screens/CardStudio';
import { ConnPill } from './ui/ConnPill';
import { SimToggle } from './ui/SimToggle';
import { HudControls } from './ui/HudControls';
import { helpStore, useHelpOpen } from './state/help';
import { historyStore, useHistoryOpen, harvestHistory } from './state/history';
import { useMapFeatureUpload } from './state/useMapFeatureUpload'; // v24 BOLT-118
import { History } from './screens/History';
import { studioStore, useStudioOpen } from './state/studio';
import { PracticeIntro } from './ui/PracticeIntro'; // v28 BOLT-151
import { PRACTICE_HINT } from '../shared/practiceCopy'; // v28 BOLT-151
import type { LngLat } from '../shared/protocol';

export function App() {
  const { state, send } = useGame();
  // v24 BOLT-118: the landmark harvest lives HERE, not in the lobby, because
  // practice auto-starts and the lobby unmounts within a second.
  useMapFeatureUpload(state, send);
  // Stream GPS once on a team/role — series players have a team before a role.
  const teamed = !!state?.you.role || !!state?.you.team;
  const sim = useSim();
  const helpOpen = useHelpOpen();
  const historyOpen = useHistoryOpen();
  const studioOpen = useStudioOpen();

  // v14 BOLT-049: harvest finished rounds into the device-local history —
  // post-round snapshots only (harvestHistory gates on intermission/ended).
  useEffect(() => {
    harvestHistory(state);
  }, [state]);

  // Real GPS streams unless the local simulator is on.
  const { sample, error } = useGeoWatch(teamed && !sim.enabled, (s) => {
    send({ t: 'position', at: { lat: s.lat, lng: s.lng }, acc: s.acc });
  });

  // v14 BOLT-048: /howto — a deep-linkable route onto the same overlay (the
  // iOS in-app WebView loads it; ?embed=1 hides the page's own close chrome).
  useEffect(() => {
    if (location.pathname === '/howto') helpStore.open();
  }, []);

  // Simulator → positions. Tapping the map sets sim.pos (see Round/Lobby).
  useEffect(() => {
    if (!sim.enabled) {
      setSimPosition(null);
      return;
    }
    setSimPosition(sim.pos);
    if (teamed && sim.pos) send({ t: 'position', at: sim.pos });
  }, [sim.enabled, sim.pos, teamed, send]);

  // v19 BOLT-088: report GPS readiness so the host's start-gate can require every
  // player connected + on precise location. Sent on a fix-state CHANGE only. The
  // web has no iOS "approximate location" mode, so a granted fix is 'precise'
  // (the server still checks accuracy); a geolocation error is 'denied'. The
  // simulator is a precise fix by construction.
  const lastReady = useRef<string>('');
  useEffect(() => {
    if (!teamed) {
      lastReady.current = '';
      return;
    }
    let auth: 'precise' | 'reduced' | 'denied' = 'denied';
    let acc: number | undefined;
    let ts = Date.now();
    if (sim.enabled) {
      auth = 'precise';
      acc = 5;
    } else if (error) {
      auth = 'denied';
    } else if (sample) {
      acc = sample.acc;
      ts = sample.ts;
      auth = 'precise';
    } else {
      return; // no fix info yet
    }
    const coarse = acc != null && acc > READINESS_MAX_ACCURACY_M;
    const key = `${auth}:${coarse ? 'coarse' : 'fine'}`;
    if (key === lastReady.current) return;
    lastReady.current = key;
    send({ t: 'readiness', locationAuth: auth, accuracyM: acc, fixAgeMs: Math.max(0, Date.now() - ts) });
  }, [teamed, sim.enabled, sample, error, send]);

  // Heartbeat: re-send the current position every 10 s so a stationary player's
  // pin stays fresh (real or simulated).
  const posRef = useRef<LngLat | null>(null);
  posRef.current = sim.enabled ? sim.pos : sample ? { lat: sample.lat, lng: sample.lng } : null;
  useEffect(() => {
    if (!teamed) return;
    const id = setInterval(() => {
      if (posRef.current) send({ t: 'position', at: posRef.current });
    }, 10_000);
    return () => clearInterval(id);
  }, [teamed, send]);

  // v5.5 §3 / v11 §2A: foreground beep + haptics on transitions. Lives HERE
  // (not in Round) so the round arc — lobby→headstart, active→intermission/
  // ended — still alerts across screen mounts; de-dup by key so a reconnect/
  // replay of an already-seen event never re-alerts.
  const prevState = useRef<PublicState | null>(null);
  const alerted = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!state) return;
    for (const e of shouldAlert(prevState.current, state, state.you.role)) {
      if (alerted.current.has(e.key)) continue;
      alerted.current.add(e.key);
      fireAlert(e.intensity);
    }
    prevState.current = state;
  }, [state]);

  // v11 §2D: a voice note from the OTHER role auto-plays on arrival
  // (foreground; the voice_note push covers backgrounded phones). Baseline
  // snapshots adopt silently; failures (autoplay policy) fall back to a ping.
  const playedNotes = useRef<Set<string>>(new Set());
  const notesBaselined = useRef(false);
  useEffect(() => {
    if (!state) return;
    const notes = state.voiceNotes ?? [];
    if (!notesBaselined.current) {
      for (const n of notes) playedNotes.current.add(n.id);
      notesBaselined.current = true;
      return;
    }
    for (const n of notes) {
      if (playedNotes.current.has(n.id)) continue;
      playedNotes.current.add(n.id);
      if (n.from === state.you.role) continue; // your own note doesn't echo
      try {
        new Audio(n.url).play().catch(() => fireAlert('ping'));
      } catch {
        fireAlert('ping');
      }
    }
  }, [state]);

  // v11 §2F: count socket opens, so every REJOIN re-triggers the subscribe
  // below — a deploy wipes the server's in-memory token store, and without
  // this a connected player silently loses pushes until an app restart.
  const [connGen, setConnGen] = useState(0);
  useEffect(() => {
    const off = socket.onStatus((s) => s === 'open' && setConnGen((g) => g + 1));
    return () => {
      off();
    };
  }, []);

  // v5: once teamed, silently (re)subscribe to Web Push if already granted.
  // The first opt-in is an explicit button in the lobby (no nagging).
  const vapid = state?.vapidPublicKey ?? null;
  useEffect(() => {
    if (teamed && vapid) void subscribePush(vapid, (sub) => send({ t: 'push_subscribe', platform: 'web', subscription: sub }), { prompt: false });
  }, [teamed, vapid, send, connGen]);

  let screen;
  if (!state) screen = <Landing />;
  else if (state.phase === 'lobby') screen = <Lobby />;
  else if (state.phase === 'firsthider') screen = <FirstHider />;
  else if (state.phase === 'intermission' || state.phase === 'ended') screen = <Score />;
  else screen = <Round />; // headstart | active

  const gpsTs = sim.enabled ? (sim.pos ? Date.now() : null) : (sample?.ts ?? null);

  // A passive "?" opens How to Play mid-round (the round keeps running beneath).
  const inRound = state?.phase === 'active' || state?.phase === 'headstart';

  // v28 BOLT-151: the practice explainer, ONCE PER PRACTICE ROUND. Keyed on the
  // room and the round index rather than a launch-scoped flag, so a second
  // practice round teaches again and a reconnect mid-round does not.
  // It lives here for the same reason the badge and the landmark harvest do:
  // practice auto-starts and `Lobby` unmounts within a second of the tap.
  const practiceRoundKey =
    state?.practice && inRound ? `${state.code}:${state.series.roundIndex}` : null;
  const [introDoneKey, setIntroDoneKey] = useState<string | null>(null);
  const showPracticeIntro = !!practiceRoundKey && introDoneKey !== practiceRoundKey;

  return (
    <div className="app">
      {state?.practice && (
        <div
          style={{
            position: 'fixed',
            top: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 60,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            pointerEvents: 'none',
          }}
        >
          <div
            data-testid="practice-badge"
            style={{
              background: 'var(--signal, #ffd400)',
              color: '#000',
              padding: '2px 12px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.1em',
            }}
          >
            PRACTICE
          </div>
          {/* v28 BOLT-151 acceptance 4: the hint STAYS for the rest of the
              round. The modal teaches it once; this reminds. It was iOS-only
              until now, which is the web-only bug in its mirror image. */}
          {inRound && (
            <div
              data-testid="practice-hint"
              style={{ fontSize: 9, color: 'var(--bone-mute)', textAlign: 'center' }}
            >
              {PRACTICE_HINT}
            </div>
          )}
        </div>
      )}
      {screen}
      {showPracticeIntro && <PracticeIntro onDismiss={() => setIntroDoneKey(practiceRoundKey)} />}
      {inRound && (
        <button className="help-chip" data-testid="help-chip" aria-label="How to play" onClick={() => helpStore.open()}>
          ?
        </button>
      )}
      {inRound ? <HudControls /> : <SimToggle />}
      <ConnPill gpsTs={gpsTs} teamed={teamed} />
      {helpOpen && (
        <HowToPlay
          onClose={() => {
            // v14 BOLT-048: leaving the /howto route restores the app root
            if (location.pathname === '/howto') history.replaceState(null, '', '/');
            helpStore.close();
          }}
        />
      )}
      {historyOpen && <History onClose={() => historyStore.close()} />}
      {studioOpen && <CardStudio onClose={() => studioStore.close()} />}
    </div>
  );
}
